import { Injectable } from '@angular/core';
import { Transaction } from '../../model/transaction';

@Injectable({
  providedIn: 'root',
})
export class TransactionsService {
  private jsFormater = (date) => new Date(date);
  private msFormater = (date) => new Date(date).getTime();
  private sorter = (_array) => _array.sort();

  public average = (elements: number[]) =>
    elements.reduce((previous, current) => previous + current, 0) /
    elements.length;

  public averageTransactions = (transactions: Transaction[]) => {
    const amounts = transactions.map((transaction) => transaction.amount);
    return this.average(amounts);
  };
  /**
   * Given an array which contains dates. This function is going to group these dates based
   * on the characteristics needed and it's going to return an array with non repited dates
   * based on that condition
   *
   * For example:
   *  Entry -> Group by Date: 1/1/12 1/1/12 3/11/13
   *  Exit -> 1/1/12  3/11/13
   *
   *  Entry -> Group by month: 1/1/12 1/1/12 3/11/13
   *  Exit -> 1/12 11/13
   *
   *  The function has not idea how the grouping is performed. It recieves that logic via function (callback)
   *  What this function knows is how to handle the transactions structure to perform the grouping operations
   */
  groupAndFormatByDates(dateFormater, transactions) {
    let groupedDates = [];

    const datesMaped = transactions.map((transaction) => transaction.date);
    const sortedDates = this.sorter(datesMaped);
    const formatedDates = sortedDates.map((date) => this.jsFormater(date));
    const selectedFormatDates = formatedDates.map(dateFormater);

    groupedDates = selectedFormatDates.filter(
      (v, i) => selectedFormatDates.indexOf(v) === i
    );
    return groupedDates;
  }

  groupByDates(transactions) {
    let groupedDates = [];

    const datesMaped = transactions.map((transaction) => transaction.date);
    const sortedDates = this.sorter(datesMaped);
    const formatedDates = sortedDates.map((date) => this.msFormater(date));

    groupedDates = formatedDates.filter(
      (v, i) => formatedDates.indexOf(v) === i
    );
    return groupedDates;
  }

  /**
   *
   * @param transactions
   * @param dateFormater
   *
   *  It recieves an array of transactions = [{}, {}, {}]
   *  Transactions contains dates in miliseconds
   *  It returns dates on a special format.
   *
   *  Entry -> transaction {
   *  date: miliseconds
   * }
   *
   * Exit -> transaction {
   * date: formatedDate
   * }
   *
   */
  transactionsDateFormater(dateFormater, transactions) {
    const formater = (transaction, dateFormater) => {
      const newTransaction = {
        ...transaction,
        date: dateFormater(transaction.date),
      };
      return newTransaction;
    };

    return transactions.map((transaction) =>
      formater(transaction, dateFormater)
    );
  }

  /**
   *  It recieves: formated dates and transactions
   *  It returns the accumulated amount by date* (formatedDates)
   */
  calculateAcumulatedByDates(formatedDates, transactionsFormated) {
    const groupedTransactions: any[] = [];
    const _dates = formatedDates;

    let acumulator = 0;
    for (let i = 0; i < formatedDates.length; i++) {
      groupedTransactions[i] = transactionsFormated.reduce(
        (sum, record) => {
          if (record.date == _dates[i]) {
            acumulator = sum + record.amount;
            return acumulator;
          } else return sum;
        },

        acumulator
      );
    }
    return groupedTransactions;
  }

  calculateExpensesGroupedByDates(formatedDates, transactionsFormated) {
    const _arrayExpenses = transactionsFormated.filter(
      (transaction) => transaction.amount < 0
    );
    return this.calculateAcumulatedByDates2(formatedDates, _arrayExpenses);
  }

  calculateIncomesGroupedByDates(formatedDates, transactionsFormated) {
    let _arrayIncomes = transactionsFormated.filter(
      (transaction) => transaction.amount > 0
    );
    return this.calculateAcumulatedByDates2(formatedDates, _arrayIncomes);
  }

  getIncomes(transactions) {
    return transactions.filter((transaction) => transaction.amount > 0);
  }

  getExpenses(transactions) {
    return transactions.filter((transaction) => transaction.amount < 0);
  }

  getAllCategories(transactions) {
    let allPieChartLabels = transactions.map(
      (transaction) => transaction.category
    );
    let labels = allPieChartLabels.filter(
      (v, i) => allPieChartLabels.indexOf(v) === i
    );
    return labels;
  }

  calculateAcumulatedByDates2(formatedDates, groupedTransactions) {
    const ngTransactions = [];
    const _dates = formatedDates;
    for (let i = 0; i < formatedDates.length; i++) {
      ngTransactions[i] = groupedTransactions.reduce(function (sum, record) {
        return record.date !== _dates[i] ? sum : sum + record.amount;
      }, 0);
    }
    return ngTransactions;
  }

  calculateAcumulatedByCategoires(categories, transactions) {
    const amountAcomulated: any[] = [];

    for (let i = 0; i < categories.length; i++) {
      let activityAmount = 0;
      for (let j = 0; j < transactions.length; j++) {
        if (categories[i] === transactions[j].category) {
          activityAmount += transactions[j].amount;
          amountAcomulated[i] = activityAmount;
        }
      }
    }
    return amountAcomulated;
  }

  getCategoriesOfThisMonth(categories, transactions) {
    const pieChartLabels: any[] = [];
    const currentMonth = new Date().getMonth() + 1;
    for (let i = 0; i < transactions.length; i++) {
      let monthRecorded = new Date(transactions[i].date).getMonth() + 1;
      for (let j = 0; j < categories.length; j++) {
        if (
          monthRecorded === currentMonth &&
          !pieChartLabels.includes(transactions[i].category)
        ) {
          pieChartLabels.push(transactions[i].category);
        }
      }
    }
    return pieChartLabels;
  }

  calculateAcumulatedByCategoriesOfThisMonth(categories, transactions) {
    const pieChartData: any[] = [];
    const currentMonth = new Date().getMonth() + 1;

    for (let i = 0; i < categories.length; i++) {
      let activityAmount = 0;
      for (let j = 0; j < transactions.length; j++) {
        let monthRecorded = new Date(transactions[j].date).getMonth() + 1;
        if (
          monthRecorded == currentMonth &&
          categories[i] === transactions[j].category
        ) {
          activityAmount += transactions[j].amount;
          pieChartData[i] = activityAmount;
        } else {
          pieChartData[i] = activityAmount;
        }
      }
    }
    return pieChartData;
  }

  getBalance(formatedDates, acumulatedByDate) {
    const currentDate = new Date();
    let amountAcumulated = -1;
    for (let i = 0; i < acumulatedByDate.length; i++) {
      const formatedDate = new Date(formatedDates[i]);
      if (currentDate.getTime() >= formatedDate.getTime()) {
        amountAcumulated = acumulatedByDate[i];
      }
    }
    return amountAcumulated;
  }

  getBalanceSpecificDay(dataSet, specificDay) {
    const currentDate = new Date(specificDay);
    let amountAcumulated = -1;
    for (let i = 0; i < dataSet.length; i++) {
      const formatedDate = new Date(dataSet[i].date);
      if (currentDate.getTime() >= formatedDate.getTime()) {
        amountAcumulated = dataSet[i].amount;
      }
    }

    return amountAcumulated;
  }
}
